import axios from 'axios'
import { getInstance } from '@router/auth'
import { permissionsService } from '@utils/permissions'
import { services } from '@utils/services'
import dayjs from 'dayjs'
import { formatPhoneNumber } from '@utils/phone-format'
import { appstreamOptions, fleets, startFleets } from '@utils/appstream.const'

const authService = getInstance()

const oneHourAgo = (d) => {
  const now = new Date().getTime()
  const hour = 1000 * 60 * 30
  const hourAgo = now - hour
  return d > hourAgo
}

export const httpServices = {
  getUsers() {
    let path = '/users/getAll'
    if (!permissionsService.isVanishedAdmin()) {
      path = `/users/org/${permissionsService.loggedInUserOrg()}`
    }
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + path, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          const users = res.data.Items.map((user, i) => {
            return {
              ...user,
              key: i,
              name: user.meta.name,
              email: user.pk.replace('USER#', ''),
              organization: user.permissions.organization,
              role:
                user?.permissions?.organization === 'vanished' &&
                user?.permissions?.role === 'admin'
                  ? 'super'
                  : user.permissions.role || 'n/a',
            }
          })
          return Promise.resolve(users)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  createUser(data) {
    return axios
      .post(process.env.VUE_APP_KONSUL_API_URL + '/users', data, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          return Promise.resolve(res.data)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  createAuth0User(data) {
    return axios
      .post(process.env.VUE_APP_KONSUL_API_URL + '/users/auth0', data, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          return Promise.resolve(res.data)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  deleteAuth0User(userId) {
    return axios
      .delete(process.env.VUE_APP_KONSUL_API_URL + `/users/auth0/${userId}`, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => Promise.resolve(res.data),
        (err) => Promise.reject(err)
      )
  },
  deleteUser(userId, orgId) {
    return axios
      .delete(
        process.env.VUE_APP_KONSUL_API_URL +
          `/users/user/${userId}/org/${orgId}`,
        {
          headers: {
            Authorization: 'Bearer ' + authService.user.access_token,
          },
        }
      )
      .then(
        (res) => Promise.resolve(res.data),
        (err) => Promise.reject(err)
      )
  },
  deleteGroup(groupId, orgId) {
    return axios
      .delete(
        process.env.VUE_APP_KONSUL_API_URL +
          `/permissions/groups/${groupId}/org/${orgId}`,
        {
          headers: {
            Authorization: 'Bearer ' + authService.user.access_token,
          },
        },
        {
          groupId,
          orgId,
        }
      )
      .then(
        (res) => Promise.resolve(res.data),
        (err) => Promise.reject(err)
      )
  },
  getGroup(groupId) {
    return axios
      .get(
        process.env.VUE_APP_KONSUL_API_URL + '/permissions/groups/' + groupId,
        {
          headers: {
            Authorization: 'Bearer ' + authService.user.access_token,
          },
        }
      )
      .then(
        (res) => {
          return res.data
        },
        () => {
          this.$notification.error({
            message: `Failed to Retrieve: User Group`,
          })
        }
      )
  },
  createGroup(formData) {
    return axios
      .post(
        process.env.VUE_APP_KONSUL_API_URL + '/permissions/groups',
        formData,
        {
          headers: {
            Authorization: 'Bearer ' + authService.user.access_token,
          },
        }
      )
      .then(
        (res) => {
          return Promise.resolve(res.data)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  getGroups() {
    let path = '/permissions/groups'
    if (!permissionsService.isVanishedAdmin()) {
      path = `/permissions/groups/org/${permissionsService.loggedInUserOrg()}`
    }
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + path, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          const groups = res.data.Items.map((group, i) => {
            return {
              ...group,
              key: i,
              name: group.pk.replace('GROUP#', ''),
              color: group.meta.color,
              users: group?.meta?.userId ? group?.meta?.userId.length : 0,
              organization: 'vanished',
            }
          })
          return Promise.resolve(groups)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  getConnections() {
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + '/connections', {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          return Promise.resolve(res.data.Items)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  getAllWorkingLinks() {
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + '/api/sms/link_working', {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          return Promise.resolve(res.data)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  getHardwares() {
    const path = '/hardwares'
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + path, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          return Promise.resolve(res.data.Items)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  updateFigment(figment) {
    const path = process.env.VUE_APP_KONSUL_API_URL + `/figments`
    return axios
      .put(path, figment, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => Promise.resolve(res.data),
        (err) => Promise.reject(err)
      )
  },
  deleteFigment(figmentId, orgId) {
    const path =
      process.env.VUE_APP_KONSUL_API_URL + `/figments/${figmentId}/org/${orgId}`
    return axios
      .delete(path, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => Promise.resolve(res.data),
        (err) => Promise.reject(err)
      )
  },
  getFigment(id) {
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + '/figments/' + id, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          return res.data
        },
        () => {
          this.$notification.error({
            message: `Failed to Retrieve: Figment`,
          })
        }
      )
  },
  async getFigments() {
    let path = '/figments'
    if (!permissionsService.isVanishedAdmin()) {
      path = `/figments/org/${permissionsService.loggedInUserOrg()}`
    }
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + path, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        async (res) => {
          const figments = res.data.Items.map((figment, i) => {
            return {
              ...figment,
              key: i,
              name:
                figment.meta.bio.firstName + ' ' + figment.meta.bio.lastName,
              mtn: figment?.meta?.sims?.mtn || 'n/a',
              phone: figment?.meta?.sims?.mtn || 'n/a',
              phoneFormatted: formatPhoneNumber(figment?.meta?.sims?.mtn),
              userId: figment.meta.userId || [],
              groupId: figment.meta.groupId || [],
              orgId: figment.meta.orgId,
              currentUsers:
                figment?.meta?.currentUsers &&
                oneHourAgo(figment?.meta?.lastAccessed)
                  ? figment.meta.currentUsers.length
                  : 0,
            }
          })

          if (permissionsService.isUser()) {
            const groups = await this.getGroups()
            const x = services.filterFigments(
              figments,
              groups,
              authService.user.email
            )
            return Promise.resolve(x)
          } else {
            return Promise.resolve(figments)
          }
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  updateContainer(container) {
    return axios
      .put(process.env.VUE_APP_KONSUL_API_URL + '/containers', container, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then((res) => {
        return Promise.resolve(res)
      })
  },
  getContainers() {
    let path = '/containers'
    if (!permissionsService.isVanishedAdmin()) {
      path = `/containers/org/${permissionsService.loggedInUserOrg()}`
    }
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + path, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          const containers = res.data.Items.map((container, i) => {
            return {
              ...container,
              key: i,
              name: container.meta.name,
              bonfireId: container.meta.bonfireId,
              hardwareId: container.meta.hardwareId.toLowerCase(),
              userId: container.meta.userId,
              groupId: container.meta.groupId,
              orgId: container.meta.orgId,
              currentUsers:
                container?.meta?.currentUsers &&
                oneHourAgo(container?.meta?.lastAccessed)
                  ? container.meta.currentUsers.length
                  : 0,
            }
          })
          return Promise.resolve(containers)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  deleteContainer(containerId, orgId) {
    const path =
      process.env.VUE_APP_KONSUL_API_URL +
      `/containers/${containerId}/org/${orgId}`
    return axios
      .delete(path, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => Promise.resolve(res.data),
        (err) => Promise.reject(err)
      )
  },
  deleteSim(id, orgId) {
    const path = process.env.VUE_APP_KONSUL_API_URL + `/sims/${id}/org/${orgId}`
    return axios
      .delete(path, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => Promise.resolve(res.data),
        (err) => Promise.reject(err)
      )
  },
  deleteHardware(id, orgId) {
    const path =
      process.env.VUE_APP_KONSUL_API_URL + `/hardwares/${id}/org/${orgId}`
    return axios
      .delete(path, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => Promise.resolve(res.data),
        (err) => Promise.reject(err)
      )
  },
  deleteConnection(id, orgId) {
    const path =
      process.env.VUE_APP_KONSUL_API_URL + `/connections/${id}/org/${orgId}`
    return axios
      .delete(path, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => Promise.resolve(res.data),
        (err) => Promise.reject(err)
      )
  },
  createHardware(data) {
    return axios
      .post(process.env.VUE_APP_KONSUL_API_URL + '/hardwares', data, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          return Promise.resolve(res.data)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  getOrgs() {
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + '/orgs', {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          const orgs = res.data.Items.map((org, i) => {
            return {
              ...org,
              key: i,
              name: org.meta.name,
              address: org.meta.address,
              license: org.meta.license,
            }
          })
          return Promise.resolve(orgs)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  getOrganization(orgId) {
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + '/orgs/' + orgId, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          return Promise.resolve(res.data)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  getHardware(hardwareId) {
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + '/hardwares/' + hardwareId, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          return Promise.resolve(res.data)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  createOrganization(data) {
    return axios
      .post(process.env.VUE_APP_KONSUL_API_URL + '/orgs', data, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          return Promise.resolve(res.data)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  deleteOrganization(orgId) {
    return axios
      .delete(process.env.VUE_APP_KONSUL_API_URL + `/orgs/${orgId}`, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => Promise.resolve(res.data),
        (err) => Promise.reject(err)
      )
  },
  getSimBank() {
    const path = '/api/sms/bank/' + '1925-FD60-4DE4'
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + path, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          return Promise.resolve(res.data)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  getSims() {
    let path = '/sims'
    if (!permissionsService.isVanishedAdmin()) {
      path = `/sims/org/${permissionsService.loggedInUserOrg()}`
    }
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + path, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => {
          const sims = res.data.Items.map((sim, i) => {
            return {
              ...sim,
              key: i,
              groupId: sim.meta.groupId,
              userId: sim.meta.userId,
              simSlot: sim.meta.simSlot,
              orgId: sim.meta.orgId,
              mtn: sim.meta.mtn,
              imsi: sim.meta.imsi || 'N/A',
              phone: sim.meta.phone,
              hardwareId: sim.meta.hardwareId,
            }
          })
          return Promise.resolve(sims)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  getConnectionsByHardware(hardwareId, cancelToken) {
    hardwareId = hardwareId.toLowerCase()
    return axios
      .get(
        process.env.VUE_APP_KONSUL_API_URL +
          `/connections/hardware/${hardwareId}`,
        {
          cancelToken,
          headers: {
            Authorization: 'Bearer ' + authService.user.access_token,
          },
        }
      )
      .then(
        (res) => {
          return Promise.resolve(res.data.Items)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  updateConnection(pk, meta, bypass) {
    return axios
      .put(
        process.env.VUE_APP_KONSUL_API_URL + '/connections',
        { pk, meta, bypass },
        {
          headers: {
            Authorization: 'Bearer ' + authService.user.access_token,
          },
        }
      )
      .then(
        (res) => {
          return Promise.resolve(res.data)
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  saveSent(message, hardwareId, theirSMS, yourSMS, failed) {
    axios.post(
      process.env.VUE_APP_KONSUL_API_URL + '/sms',
      {
        theirSMS: theirSMS,
        yourSMS: yourSMS.mtn,
        connectionId: yourSMS.connectionId,
        email: authService.user.email,
        hardwareId: hardwareId,
        outgoing: true,
        content: failed
          ? 'Failed: ' + message?.data?.text
          : message?.data?.text,
      },
      {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      }
    )
  },
  getAWSMessagesPaginate(hardwareId, cancelToken, startKey) {
    return axios
      .post(
        process.env.VUE_APP_KONSUL_API_URL + '/sms/paginate',
        {
          hardwareId,
          startKey,
          limit: '100',
        },
        {
          cancelToken,
          headers: {
            Authorization: 'Bearer ' + authService.user.access_token,
          },
        }
      )
      .then(
        (res) => {
          if (res.data.Items) {
            const messages = res.data.Items.map((m) => {
              return {
                ...m,
                type: 'text',
                author: 'me',
                date: dayjs(m.timestamp).format('YYYY-MM-DDTHH:mm'),
                timestamp: m.timestamp,
                data: {
                  text: m?.content || '',
                  meta:
                    m?.yourSMS +
                    ` · ` +
                    dayjs(m.timestamp).format('YYYY-MM-DDTHH:mm'),
                },
              }
            })
            const lastEvaluatedKey = res?.data?.LastEvaluatedKey
            return Promise.resolve({ messages, lastEvaluatedKey })
          } else {
            return Promise.resolve([])
          }
        },
        (err) => {
          return Promise.reject(err)
        }
      )
  },
  async startFleets() {
    const res = await Promise.all(
      startFleets.map(async (f) => {
        const options = {
          method: 'POST',
          url: 'https://aevfegda8g.execute-api.us-east-1.amazonaws.com/default/appstream-start-stop',
          headers: { 'Content-Type': 'application/json' },
          data: { method: 'START', name: f },
        }
        const data = await axios.request(options)
        return data
      })
    )
    return res
  },
  async stopFleets() {
    const res = await Promise.all(
      fleets.map(async (f) => {
        const options = {
          method: 'POST',
          url: 'https://aevfegda8g.execute-api.us-east-1.amazonaws.com/default/appstream-start-stop',
          headers: { 'Content-Type': 'application/json' },
          data: { method: 'STOP', name: f },
        }
        const data = await axios.request(options)
        return data
      })
    )
    return res
  },
  async startFleet(f) {
    const { FleetName } = appstreamOptions.filter(
      (o) => o.bonfireId === f.meta.bonfireId
    )[0]
    if (FleetName) {
      const options = {
        method: 'POST',
        url: 'https://aevfegda8g.execute-api.us-east-1.amazonaws.com/default/appstream-start-stop',
        headers: { 'Content-Type': 'application/json' },
        data: { method: 'START', name: FleetName },
      }
      const data = await axios.request(options)
      return data
    }
  },
  async stopFleet(f) {
    const { FleetName } = appstreamOptions.filter(
      (o) => o.bonfireId === f.meta.bonfireId
    )[0]
    if (FleetName) {
      const options = {
        method: 'POST',
        url: 'https://aevfegda8g.execute-api.us-east-1.amazonaws.com/default/appstream-start-stop',
        headers: { 'Content-Type': 'application/json' },
        data: { method: 'STOP', name: FleetName },
      }
      const data = await axios.request(options)
      return data
    }
  },
  getFleetStatus() {
    const options = {
      method: 'POST',
      url: 'https://aevfegda8g.execute-api.us-east-1.amazonaws.com/default/appstream-start-stop',
      headers: { 'Content-Type': 'application/json' },
      data: { method: 'STATUS', name: '' },
    }

    return axios
      .request(options)
      .then(function (response) {
        const fleetStatus = response.data.Fleets
        fleetStatus.forEach((f) => {
          const x = appstreamOptions.filter((o) => o.FleetName === f.Name)[0]
          if (!x) {
            f.bonfireId = f.Name
          } else {
            f.bonfireId = x.bonfireId
          }
        })
        return fleetStatus
      })
      .catch(function (error) {
        return error
      })
  },
  getAppStreamMap() {
    return axios
      .get(`${process.env.VUE_APP_KONSUL_APPSTREAM_URL}`, {
        params: { appId: '12', userId: '12' },
      })
      .then((res) => {
        return Promise.resolve(res.data)
      })
  },
  resetHardware(hardwareId) {
    return axios
      .get(
        process.env.VUE_APP_KONSUL_API_URL +
          `/api/hardware/${hardwareId}/reset`,
        {
          headers: {
            Authorization: 'Bearer ' + authService.user.access_token,
          },
        }
      )
      .then((response) => {
        return response.data
      })
      .catch((error) => {
        return error
      })
  },
  getHardwareStatus() {
    return axios
      .get(process.env.VUE_APP_KONSUL_API_URL + '/api/hardware/status', {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then((response) => {
        return response.data
      })
      .catch((error) => {
        return error
      })
  },
  deleteMessage(pk, sk) {
    return axios
      .delete(process.env.VUE_APP_KONSUL_API_URL + `/sms/${pk}/${sk}`, {
        headers: {
          Authorization: 'Bearer ' + authService.user.access_token,
        },
      })
      .then(
        (res) => Promise.resolve(res.data),
        (err) => Promise.reject(err)
      )
  },
  deleteMessagesFromDevice(sn) {
    return axios
      .delete(
        process.env.VUE_APP_KONSUL_API_URL + `/api/sms/messages/delete/${sn}`,
        {
          headers: {
            Authorization: 'Bearer ' + authService.user.access_token,
          },
        }
      )
      .then(
        (res) => Promise.resolve(res.data),
        (err) => Promise.reject(err)
      )
  },
}
